import { css } from 'lit';
import { HttpStatusCode } from '@/constants';
import { setHHCurrentEditors } from '@/redux/hh/hh-actions-current-editors';
import { selectHHCurrentDetailsChanged } from '@/redux/hh/hh-selectors';
import { getSessionUUID } from '@/utils/SessionStorageUtils';
import { ConfigHHApi } from '../../config/ConfigHH';
import { APIRequestReturn } from '../../redux/APIRequest';
import { refreshHH } from '../../redux/hh/redux-hh';
import store from '../../redux/redux-store';

let HHPollingEnabled = false;
let HHPollingTimeout: ReturnType<typeof setTimeout> | undefined;
let HHPollingDelayMs: number;
let HHLastAPIRequestReturn: APIRequestReturn;
let HHPollingId: number;

export const pollHH = async (): Promise<void> => {
	const apiRequestReturn = await store.dispatch(refreshHH(HHPollingId));
	HHLastAPIRequestReturn = apiRequestReturn;

	if (HHPollingEnabled) {
		HHPollingTimeout = setTimeout(() => {
			void pollHH();
		}, HHPollingDelayMs);
	}
};

export const stopPollingHH = (autoAbort = true): void => {
	HHPollingEnabled = false;
	if (HHPollingTimeout !== undefined) {
		clearTimeout(HHPollingTimeout);
	}
	HHPollingTimeout = undefined;

	if (autoAbort && HHLastAPIRequestReturn) {
		HHLastAPIRequestReturn.abortController?.abort();
	}
};

export const startPollingHH = async (
	hhId: number,
	pollingDelayMs = ConfigHHApi.pollingRate,
	pollImmediately = true,
): Promise<void> => {
	if (HHPollingEnabled) {
		return;
	}

	HHPollingEnabled = true;
	HHPollingDelayMs = pollingDelayMs;
	HHPollingId = hhId;

	if (pollImmediately) {
		await pollHH();
	} else {
		HHPollingTimeout = setTimeout(() => {
			void pollHH();
		}, HHPollingDelayMs);
	}
};

let HeartbeatEnabled = false;
let HeartbeatTimeout: ReturnType<typeof setTimeout>;
let HeartbeatDelayMs: number;
let HeartbeatLastAPIRequestReturn: APIRequestReturn;
let HeartbeatId: number;
let HeartbeatISReadOnly: boolean;

function HeartbeatVisibilityChangeHandler() {
	const currentDetailsChanged = selectHHCurrentDetailsChanged(store.getState());
	if (!document.hidden && !HeartbeatISReadOnly && !currentDetailsChanged) {
		// eslint-disable-next-line promise/prefer-await-to-then
		void store.dispatch(setHHCurrentEditors(HeartbeatId, getSessionUUID())).then((ret) => {
			HeartbeatLastAPIRequestReturn = ret;
			return ret;
		});
	}
}

export const stopHighwayHelperHeartbeat = (autoAbort = true): void => {
	HeartbeatEnabled = false;
	window.clearTimeout(HeartbeatTimeout);
	if (autoAbort && HeartbeatLastAPIRequestReturn) {
		HeartbeatLastAPIRequestReturn.abortController?.abort();
	}
	document.removeEventListener('visibilitychange', HeartbeatVisibilityChangeHandler);
};

export const sendHighwayHelperHeartbeat = async (isReadOnly = false): Promise<void> => {
	// don't alert other users that a change might be coming to their form if this form is out of date already
	const currentDetailsChanged = selectHHCurrentDetailsChanged(store.getState());
	if (!isReadOnly && !currentDetailsChanged) {
		HeartbeatLastAPIRequestReturn = await store.dispatch(
			setHHCurrentEditors(HeartbeatId, getSessionUUID()),
		);
		if (HeartbeatLastAPIRequestReturn.response?.status === HttpStatusCode.UNAUTHORIZED) {
			stopHighwayHelperHeartbeat();
		}
	}
	if (HeartbeatEnabled === true) {
		HeartbeatTimeout = setTimeout(() => {
			void sendHighwayHelperHeartbeat(isReadOnly);
		}, HeartbeatDelayMs);
	}
};

export const startHighwayHelperHeartbeat = (
	id: number,
	delay: number,
	isReadOnly = false,
): void => {
	if (HeartbeatEnabled === true) {
		return; //	no overlapping polling!
	}
	HeartbeatEnabled = true;
	HeartbeatDelayMs = delay;
	HeartbeatId = id;
	HeartbeatISReadOnly = isReadOnly;
	void sendHighwayHelperHeartbeat(isReadOnly);

	document.addEventListener('visibilitychange', HeartbeatVisibilityChangeHandler);
};
